import { FieldValues, Path, PathValue, useController, useFormContext } from 'react-hook-form';
import { FormItem, FormField } from '@/components/ui/form';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { capitalizeFirstLetter } from '@/lib/utils';
import { Label } from './ui/label';

const phoneUtil = PhoneNumberUtil.getInstance();

export const getPhoneNumberValue = (value: string | undefined | null) => {
  if (!value) return undefined;
  try {
    const number = phoneUtil.parseAndKeepRawInput(value);
    if (phoneUtil.isValidNumber(number)) return value;
    else return undefined;
  } catch {
    return undefined;
  }
};

export const createPhoneSchema = (required: boolean, fieldName = 'Phone number') => {
  return z
    .string()
    .optional()
    .nullable()
    .superRefine((data, ctx) => {
      if (required) {
        if (!data || data.trim() === '') {
          ctx.addIssue({
            message: `${fieldName} is required`,
            path: [],
            code: 'custom',
          });
          return;
        }

        try {
          const number = phoneUtil.parseAndKeepRawInput(data);
          const countryCode = `+${number.getCountryCode()}`;
          if (data.trim() === countryCode) {
            ctx.addIssue({
              message: `${fieldName} is required`,
              path: [],
              code: 'custom',
            });
            return;
          }
        } catch (e) {
          ctx.addIssue({
            message: `${fieldName} is required`,
            path: [],
            code: 'custom',
          });
          return;
        }
      }
      if (data && data.length > 5) {
        const number = phoneUtil.parseAndKeepRawInput(data);
        if (!phoneUtil.isValidNumber(number)) {
          ctx.addIssue({
            message: `${fieldName} is invalid`,
            path: [],
            code: 'custom',
          });
        }
      }
    });
};

interface FormFieldPhoneProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  defaultCountry?: string;
  required?: boolean;
  onChange?: (value: string) => void;
  autofocus?: boolean;
  itemClassName?: string;
  placeholder?: string;
  tooltip?: string;
  disabled?: boolean;
}

const FormFieldPhone = <T extends FieldValues>({
  name,
  label,
  defaultCountry,
  required = false,
  autofocus,
  itemClassName,
  onChange,
  placeholder,
  tooltip,
  disabled,
}: FormFieldPhoneProps<T>) => {
  const { control, trigger, setError, clearErrors, setValue } = useFormContext<T>();
  const { field, fieldState } = useController({ name, control });
  const [focus, setFocus] = useState(false);
  const [dialCode, setDialCode] = useState<string | undefined>();
  const isHydratingRef = useRef(true);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      isHydratingRef.current = false;
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, []);

  const validate = () => {
    if (fieldState.isTouched && field.value && dialCode) {
      if ((field?.value as string).trim() !== `+${dialCode}`) {
        try {
          const number = phoneUtil.parseAndKeepRawInput(field.value);
          if (!phoneUtil.isValidNumber(number)) {
            setError(name, {
              type: 'required',
              message: `${capitalizeFirstLetter(label)} is invalid`,
            });
          } else {
            clearErrors(name);
          }
        } catch (e) {
          setError(name, {
            type: 'required',
            message: `${capitalizeFirstLetter(label)} is invalid`,
          });
        }
      }
    }
  };

  return (
    <FormItem className={`flex flex-col gap-1 ${itemClassName ? itemClassName : ''}`}>
      <Label htmlFor={name} className="block text-sm font-medium">
        {label}
        {required && <span className="text-error align-top">*</span>}
        {tooltip && (
          <div className="group relative inline-flex items-center ml-1">
            <span className="cursor-pointer text-muted-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-info"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
            </span>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden w-max max-w-xs rounded bg-popover px-2 py-1 text-sm text-popover-foreground shadow-md group-hover:block z-50 border">
              {tooltip}
            </div>
          </div>
        )}
      </Label>
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <>
            <PhoneInput
              className="w-full"
              autoFocus={autofocus}
              defaultCountry={defaultCountry}
              placeholder={placeholder}
              disabled={disabled}
              value={field.value || ''}
              data-testid={`form-field-phone-${name}`}
              onChange={(e, meta) => {
                setDialCode(meta.country.dialCode);
                const dialCodeValue = `+${meta.country.dialCode}`;
                const incomingValue = e.trim();
                if (dialCodeValue === incomingValue) {
                  if (
                    (isHydratingRef.current || !isFocusedRef.current) &&
                    field.value &&
                    String(field.value).trim() !== incomingValue
                  ) {
                    return;
                  }
                  // This prevents the form from being marked as dirty on mount, as onChange is triggered immediately when the component mounts, so we set the initial value manually without affecting the dirty state.
                  setValue(name, e as PathValue<T, typeof name>, {
                    shouldDirty: false,
                  });
                } else field.onChange(e);
                onChange && onChange(e);
              }}
              onFocus={() => {
                isFocusedRef.current = true;
                setFocus(true);
              }}
              onBlur={async () => {
                isFocusedRef.current = false;
                setFocus(false);
                field.onBlur();
                await trigger(name);
                validate();
              }}
              countrySelectorStyleProps={{
                buttonClassName: `!h-12 !p-2 focus:outline-none !rounded-l-md focus:ring-[0.5px] focus:ring-focuscolor ${focus ? '!border-focuscolor' : '!border-bordercolor'} `,
              }}
              inputClassName={`flex !h-12 w-full !rounded-r-md !px-3 !py-1 !text-sm !font-[400] !border-[1px] ${focus ? '!border-focuscolor' : '!border-bordercolor'} !disabled:cursor-not-allowed !disabled:opacity-50 !focus-visible:border-focuscolor !bg-white`}
            />
            {fieldState.error && <p className="text-error text-xs">{fieldState.error.message}</p>}
          </>
        )}
      />
    </FormItem>
  );
};

export default FormFieldPhone;
